import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DxPopupComponent} from "devextreme-angular/ui/popup";
import {DetectMethodChanges, isVoid, wrapInPromise} from "../utils";
import {
    GetPivotsData, GetPivotsDataReply,
    GetRecentPivots,
    GetRecentPivotsReply, GetTrendSettings, GetTrendSettingsReply,
    PivotPointDescriptor, SaveTrendSettings
} from "../shell-communication/shell-operations-protocol";
import {ShellClientService} from "../shell-communication/shell-client.service";
import {MessageBusService} from "../message-bus.service";
import {ApplicationSettingsService} from "../app-settings/application-settings.service";
import { SessionService } from '../authentication/session-service.service';
import {TrendInformation} from "./model";

export interface PivotsRow {
    upper?: PivotPointDescriptor;
    lower?: PivotPointDescriptor;
}

export interface DataUnitModel {
    header: string;
    oneMinRow: PivotsRow;
    fiveMinRow: PivotsRow;
    tenMinRow: PivotsRow;
    fifteenMinRow: PivotsRow;
}

export interface TrendDataModel {
    header: string;
    oneMinTrend: string;
    fiveMinTrend: string;
    tenMinTrend: string;
    fifteenMinTrend: string;
    oneMinInfo?: TrendInformation
    fiveMinInfo?: TrendInformation
    tenMinInfo?: TrendInformation
    fifteenMinInfo?: TrendInformation
}

export interface TrendSettings {
    timeframe?: string;
    emaLength?: number;
    emaCloseConfirmationFilter?: number;
    slopeLookBackPeriod?: number;
    slopeThreshold?: number;
    rangeChopFilterWidth?: number;
    rangeFilterPeriod?: number;
}

export interface CollapsibleMatrix {
    columnUpCollapsed?: boolean;
    columnDownCollapsed?: boolean;
    columnRecentCollapsed?: boolean;
    columnTrendsCollapsed?: boolean;
    rowUpperCollapsed?: boolean;
    rowBottomCollapsed?: boolean;
}

@Component({
    selector: 'ets-pivot-points-popup',
    templateUrl: 'pivot-points-popup.component.html',
    styleUrls: ['pivot-points-popup.component.scss']
})

export class PivotPointsPopupComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _shellClient: ShellClientService,
        private readonly _appSettings: ApplicationSettingsService,
        private readonly _sessionService: SessionService,
    ) {
    }

    private _allDescriptors: PivotPointDescriptor[] = [];
    private _timeoutId: number;

    @ViewChild(DxPopupComponent)
    popup: DxPopupComponent;

    isLoading = false;

    upTrendClose: DataUnitModel;
    upTrendWicks: DataUnitModel;
    downTrendClose: DataUnitModel;
    downTrendWicks: DataUnitModel;
    mostRecentClose: DataUnitModel;
    mostRecentWicks: DataUnitModel;
    trendData: TrendDataModel

    trendSettings: TrendSettings = {};

    collapsibleMatrix: CollapsibleMatrix = {};

    wicksRowVisible = true;

    get trendIcon() {

        const preferredTrendTimeframe = this._appSettings.preferredTrendTimeframe;
        if (isVoid(preferredTrendTimeframe)) {
            return undefined;
        }

        let trend;

        if (preferredTrendTimeframe === '1 min') {
            trend = this.trendData.oneMinTrend;
        } else if (preferredTrendTimeframe === '5 min') {
            trend = this.trendData.fiveMinTrend;
        } else if (preferredTrendTimeframe === '10 min') {
            trend = this.trendData.tenMinTrend;
        } else if (preferredTrendTimeframe === '15 min') {
            trend = this.trendData.fifteenMinTrend;
        }

        switch (trend) {
            case 'Up':
                return 'arrowup';
            case 'Down':
                return 'arrowdown';
            case 'Flat':
                return 'isnotblank';
            case 'Undefined':
                return 'minus';
        }
    }

    get trendIconClass() {
        let trend = this.trendIcon;

        switch (trend) {
            case 'arrowup':
                return 'trend-up';
            case 'arrowdown':
                return 'trend-down';
            case 'isnotblank':
                return 'trend-flat';
            case 'minus':
                return 'trend-undefined';
        }
    }

    get canChangeTrendSettings() {
        return this._sessionService.isSuperUser;
    }


    async ngOnInit() {
        this.setupData();
        this.loadPivots();
    }

    ngOnDestroy() {
        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
        }
    }

    @DetectMethodChanges()
    async showPopup() {
        this.popup.visible = true;
    }

    @DetectMethodChanges()
    onHidden() {
        this.popup.visible = false;
    }

    @DetectMethodChanges()
    loadPivots() {
        const self = this;
        const qry = new GetPivotsData();
        this._shellClient.processQuery<GetPivotsDataReply>(qry)
            .then((data) => {
                this.onPivotDataLoaded(data);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                this.isLoading = false;
                this._changeDetector.detectChanges();
                self._timeoutId = setTimeout(() => this.loadPivots(), 3 * 1000) as any;
            });
    }

    private setupData() {
        this._allDescriptors.length = 0;

        const ticker = 'SPX';

        this.upTrendClose = {
            header: "Close",
            oneMinRow: {
                upper: {timeframe: '1 min', trend: 'Up', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {timeframe: '1 min', trend: 'Up', pivotSource: 'Close', direction: 'Down', ticker}
            },
            fiveMinRow: {
                upper: {timeframe: '5 min', trend: 'Up', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {timeframe: '5 min', trend: 'Up', pivotSource: 'Close', direction: 'Down', ticker}
            },
            tenMinRow: {
                upper: {timeframe: '10 min', trend: 'Up', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {timeframe: '10 min', trend: 'Up', pivotSource: 'Close', direction: 'Down', ticker}
            },
            fifteenMinRow: {
                upper: {timeframe: '15 min', trend: 'Up', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {timeframe: '15 min', trend: 'Up', pivotSource: 'Close', direction: 'Down', ticker}
            },
        };
        this.addToDescriptors(this.upTrendClose);

        this.upTrendWicks = {
            header: "Wicks",
            oneMinRow: {
                upper: {timeframe: '1 min', trend: 'Up', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {timeframe: '1 min', trend: 'Up', pivotSource: 'Wicks', direction: 'Down', ticker}
            },
            fiveMinRow: {
                upper: {timeframe: '5 min', trend: 'Up', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {timeframe: '5 min', trend: 'Up', pivotSource: 'Wicks', direction: 'Down', ticker}
            },
            tenMinRow: {
                upper: {timeframe: '10 min', trend: 'Up', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {timeframe: '10 min', trend: 'Up', pivotSource: 'Wicks', direction: 'Down', ticker}
            },
            fifteenMinRow: {
                upper: {timeframe: '15 min', trend: 'Up', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {timeframe: '15 min', trend: 'Up', pivotSource: 'Wicks', direction: 'Down', ticker}
            },
        };
        this.addToDescriptors(this.upTrendWicks);

        this.downTrendClose = {
            header: "Close",
            oneMinRow: {
                upper: {timeframe: '1 min', trend: 'Down', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {timeframe: '1 min', trend: 'Down', pivotSource: 'Close', direction: 'Down', ticker}
            },
            fiveMinRow: {
                upper: {timeframe: '5 min', trend: 'Down', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {timeframe: '5 min', trend: 'Down', pivotSource: 'Close', direction: 'Down', ticker}
            },
            tenMinRow: {
                upper: {timeframe: '10 min', trend: 'Down', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {timeframe: '10 min', trend: 'Down', pivotSource: 'Close', direction: 'Down', ticker}
            },
            fifteenMinRow: {
                upper: {timeframe: '15 min', trend: 'Down', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {timeframe: '15 min', trend: 'Down', pivotSource: 'Close', direction: 'Down', ticker}
            },
        };
        this.addToDescriptors(this.downTrendClose)

        this.downTrendWicks = {
            header: "Wicks",
            oneMinRow: {
                upper: {timeframe: '1 min', trend: 'Down', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {timeframe: '1 min', trend: 'Down', pivotSource: 'Wicks', direction: 'Down', ticker}
            },
            fiveMinRow: {
                upper: {timeframe: '5 min', trend: 'Down', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {timeframe: '5 min', trend: 'Down', pivotSource: 'Wicks', direction: 'Down', ticker}
            },
            tenMinRow: {
                upper: {timeframe: '10 min', trend: 'Down', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {timeframe: '10 min', trend: 'Down', pivotSource: 'Wicks', direction: 'Down', ticker}
            },
            fifteenMinRow: {
                upper: {timeframe: '15 min', trend: 'Down', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {timeframe: '15 min', trend: 'Down', pivotSource: 'Wicks', direction: 'Down', ticker}
            },
        };
        this.addToDescriptors(this.downTrendWicks);

        this.mostRecentClose = {
            header: "Close",
            oneMinRow: {
                upper: {timeframe: '1 min', trend: 'Most Recent', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {
                    timeframe: '1 min',
                    trend: 'Most Recent',
                    pivotSource: 'Close',
                    direction: 'Down',
                    ticker
                }
            },
            fiveMinRow: {
                upper: {timeframe: '5 min', trend: 'Most Recent', pivotSource: 'Close', direction: 'Up', ticker},
                lower: {
                    timeframe: '5 min',
                    trend: 'Most Recent',
                    pivotSource: 'Close',
                    direction: 'Down',
                    ticker
                }
            },
            tenMinRow: {
                upper: {
                    timeframe: '10 min',
                    trend: 'Most Recent',
                    pivotSource: 'Close',
                    direction: 'Up',
                    ticker
                },
                lower: {
                    timeframe: '10 min',
                    trend: 'Most Recent',
                    pivotSource: 'Close',
                    direction: 'Down',
                    ticker
                }
            },
            fifteenMinRow: {
                upper: {
                    timeframe: '15 min',
                    trend: 'Most Recent',
                    pivotSource: 'Close',
                    direction: 'Up',
                    ticker
                },
                lower: {
                    timeframe: '15 min',
                    trend: 'Most Recent',
                    pivotSource: 'Close',
                    direction: 'Down',
                    ticker
                }
            },
        };
        this.addToDescriptors(this.mostRecentClose);

        this.mostRecentWicks = {
            header: "Wicks",
            oneMinRow: {
                upper: {timeframe: '1 min', trend: 'Most Recent', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {
                    timeframe: '1 min',
                    trend: 'Most Recent',
                    pivotSource: 'Wicks',
                    direction: 'Down',
                    ticker
                }
            },
            fiveMinRow: {
                upper: {timeframe: '5 min', trend: 'Most Recent', pivotSource: 'Wicks', direction: 'Up', ticker},
                lower: {
                    timeframe: '5 min',
                    trend: 'Most Recent',
                    pivotSource: 'Wicks',
                    direction: 'Down',
                    ticker
                }
            },
            tenMinRow: {
                upper: {
                    timeframe: '10 min',
                    trend: 'Most Recent',
                    pivotSource: 'Wicks',
                    direction: 'Up',
                    ticker
                },
                lower: {
                    timeframe: '10 min',
                    trend: 'Most Recent',
                    pivotSource: 'Wicks',
                    direction: 'Down',
                    ticker
                }
            },
            fifteenMinRow: {
                upper: {
                    timeframe: '15 min',
                    trend: 'Most Recent',
                    pivotSource: 'Wicks',
                    direction: 'Up',
                    ticker
                },
                lower: {
                    timeframe: '15 min',
                    trend: 'Most Recent',
                    pivotSource: 'Wicks',
                    direction: 'Down',
                    ticker
                }
            },
        }
        this.addToDescriptors(this.mostRecentWicks);

        this.trendData = {
            header: "Indicator",
            oneMinTrend: "",
            fiveMinTrend: "",
            tenMinTrend: "",
            fifteenMinTrend: ""
        };
    }

    private addToDescriptors(data: DataUnitModel) {
        this._allDescriptors.push(data.oneMinRow.upper);
        this._allDescriptors.push(data.oneMinRow.lower);
        this._allDescriptors.push(data.fiveMinRow.upper);
        this._allDescriptors.push(data.fiveMinRow.lower);
        this._allDescriptors.push(data.tenMinRow.upper);
        this._allDescriptors.push(data.tenMinRow.lower);
        this._allDescriptors.push(data.fifteenMinRow.upper);
        this._allDescriptors.push(data.fifteenMinRow.lower);
    }

    @DetectMethodChanges()
    private onPivotDataLoaded(data: GetPivotsDataReply) {

        data.pivots.forEach(pp => {
            const match = this._allDescriptors.find(x => {
                const same = pp.ticker == x.ticker &&
                    pp.timeframe == x.timeframe &&
                    pp.trend == x.trend &&
                    pp.direction == x.direction &&
                    pp.pivotSource == x.pivotSource;

                return same;
            });

            if (isVoid(match)) {
                return;
            }

            match.pivotPoint = pp.pivotPoint;
            match.roundedPivotPoint = Math.round(pp.pivotPoint);
            match.barTime = pp.barTime;

        });


        data.trends.forEach(trend => {
            switch (trend.timeframe) {
                case '1 min':
                    this.trendData.oneMinTrend = trend.trend;
                    this.trendData.oneMinInfo = trend.trendInformation;
                    break;
                case '5 min':
                    this.trendData.fiveMinTrend = trend.trend;
                    this.trendData.fiveMinInfo = trend.trendInformation;
                    break;
                case '10 min':
                    this.trendData.tenMinTrend = trend.trend;
                    this.trendData.tenMinInfo = trend.trendInformation;
                    break;
                case '15 min':
                    this.trendData.fifteenMinTrend = trend.trend;
                    this.trendData.fifteenMinInfo = trend.trendInformation;
                    break;
                default:
                    break;
            }
        });
    }

    @ViewChild('trendSettingsPopup')
    trendSettingsPopup: DxPopupComponent;

    @DetectMethodChanges()
    showTrendSettings(timeframe: string) {
        this.isLoading = true;

        const qry = new GetTrendSettings(timeframe);

        this._shellClient.processQuery<GetTrendSettingsReply>(qry)
            .then((reply) => {
                this.trendSettings = reply.trendSettings;
                this.trendSettingsPopup.title = `Trend Settings for ${timeframe}`;
                this.trendSettingsPopup.visible = true;
            }).finally(() => {
            this.isLoading = false;
            this._changeDetector.detectChanges();
        });
    }

    @DetectMethodChanges()
    hideTrendSettings() {
        this.trendSettingsPopup.visible = false;
    }

    saveTrendSettings() {
        this.isLoading = true;
        const cmd = new SaveTrendSettings(this.trendSettings);
        this._shellClient.processCommand(cmd)
            .then(() => {
                this.hideTrendSettings();
            }).finally(() => {
            this.isLoading = false;
            this._changeDetector.detectChanges();
        });
    }

    @DetectMethodChanges()
    toggleHeader(dataObj: DataUnitModel) {
    }

    @DetectMethodChanges()
    toggleColumn(specificity: 'up' | 'down' | 'recent' | 'trends') {
        if (specificity === 'up') {
            this.collapsibleMatrix.columnUpCollapsed = !this.collapsibleMatrix.columnUpCollapsed;
            if (this.collapsibleMatrix.columnUpCollapsed) {
                if (this.getVisibleColumnsCount() === 0) {
                    this.collapsibleMatrix.columnUpCollapsed = false;
                }
            }
        } else if (specificity === 'down') {
            this.collapsibleMatrix.columnDownCollapsed = !this.collapsibleMatrix.columnDownCollapsed;
            if (this.collapsibleMatrix.columnDownCollapsed) {
                if (this.getVisibleColumnsCount() === 0) {
                    this.collapsibleMatrix.columnDownCollapsed = false;
                }
            }
        } else if (specificity === 'recent') {
            this.collapsibleMatrix.columnRecentCollapsed = !this.collapsibleMatrix.columnRecentCollapsed;
            if (this.collapsibleMatrix.columnRecentCollapsed) {
                if (this.getVisibleColumnsCount() === 0) {
                    this.collapsibleMatrix.columnRecentCollapsed = false;
                }
            }
        } else if (specificity === 'trends') {
            this.collapsibleMatrix.columnTrendsCollapsed = !this.collapsibleMatrix.columnTrendsCollapsed;
            if (this.collapsibleMatrix.columnTrendsCollapsed) {
                if (this.getVisibleColumnsCount() === 0) {
                    this.collapsibleMatrix.columnTrendsCollapsed = false;
                }
            }
        }

    }

    getPopupWidth() {
        const columnUp = this.collapsibleMatrix.columnUpCollapsed ? 28 : 258;
        const columnDown = this.collapsibleMatrix.columnDownCollapsed ? 28 : 258;
        const columnRecent = this.collapsibleMatrix.columnRecentCollapsed ? 28 : 258;
        const columnTrends = this.collapsibleMatrix.columnTrendsCollapsed ? 28 : 258;

        return (columnUp + columnDown + columnRecent + columnTrends) + 50;
    }

    getPopupHeight() {
        const visibleColumnsCount = this.getVisibleColumnsCount();

        return (visibleColumnsCount === 0 || !this.wicksRowVisible) ? 250 : 450;
    }

    private getVisibleColumnsCount() {
        const columnUp = this.collapsibleMatrix.columnUpCollapsed ? 0 : 1;
        const columnDown = this.collapsibleMatrix.columnDownCollapsed ? 0 : 1;
        const columnRecent = this.collapsibleMatrix.columnRecentCollapsed ? 0 : 1;
        const columnTrends = this.collapsibleMatrix.columnTrendsCollapsed ? 0 : 1;

        const visibleColumnsCount = columnUp + columnDown + columnRecent + columnTrends;
        return visibleColumnsCount;
    }

    closeWindow() {
        this.onHidden();
    }

    @DetectMethodChanges()
    onWicksRowVisibleChanged() {
    }
}